import {createBrowserRouter, Navigate} from 'react-router-dom';
import {
  AppShell, AuthLayout, LoginView, ForgotPasswordView, ResetPasswordView,
  VerifyEmailView, ConfirmEmailChangeView
} from './components/AppShell';
import {Dashboard} from './pages/Dashboard';
import {Profiles} from './pages/Profiles';
import {ProfileDetail} from './pages/ProfileDetail';
import {FinancialYearDetail} from './pages/FinancialYearDetail';
import {Documents} from './pages/Documents';
import {Settings, UserGuide} from './pages/Settings';
import {getRepository} from './data/repository';

function ProtectedRoute({children}: {children: React.ReactNode}) {
  const user = getRepository().getCurrentUser();
  if (!user) return <Navigate to="/auth/login" replace />;
  return <>{children}</>;
}

function PublicRoute({children}: {children: React.ReactNode}) {
  const user = getRepository().getCurrentUser();
  if (user) return <Navigate to="/" replace />;
  return <>{children}</>;
}

function NotFoundContent() {
  return (
    <div className="flex flex-col items-center justify-center min-h-[60vh] text-center p-8 space-y-4">
      <div className="w-16 h-16 bg-slate-100 rounded-2xl flex items-center justify-center text-slate-400 mb-2">
        <span className="text-2xl font-bold">404</span>
      </div>
      <h2 className="text-xl font-semibold text-slate-900">Path Not Found</h2>
      <p className="text-slate-500 max-w-sm">The vault route you requested does not exist or is currently restricted.</p>
    </div>
  );
}

export const router = createBrowserRouter([
  {
    path: '/auth',
    element: <PublicRoute><AuthLayout /></PublicRoute>,
    children: [
      {path: 'login', element: <LoginView />},
      {path: 'forgot-password', element: <ForgotPasswordView />},
      {path: 'reset-password', element: <ResetPasswordView />},
      {path: 'verify-email', element: <VerifyEmailView />},
      {path: 'verify', element: <VerifyEmailView />},
      {path: 'confirm-email-change', element: <ConfirmEmailChangeView />},
      {path: '*', element: <Navigate to="/auth/login" replace />}
    ],
  },
  {
    path: '/',
    element: <ProtectedRoute><AppShell /></ProtectedRoute>,
    children: [
      {index: true, element: <Dashboard />},
      {path: 'profiles', element: <Profiles />},
      {path: 'profiles/:profileId', element: <ProfileDetail />},
      {path: 'profiles/:profileId/financial-years/:financialYearId', element: <FinancialYearDetail />},
      {path: 'documents', element: <Documents />},
      {
        path: 'reports',
        lazy: async () => {
          const module = await import('./reports/profileFinancialReport');
          return {Component: module.Reports};
        }
      },
      {path: 'settings', element: <Settings />},
      {path: 'settings/guide', element: <UserGuide />},
      {path: '*', element: <NotFoundContent />},
    ],
  },
]);